import { faBars } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { AppBar, Box, Stack, Toolbar, Drawer } from '@vapor/material'
import { IconButton } from '@mui/material'
import { HICLoading } from 'hic-components'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { hicContext } from '../../hooks/context/context'
import { styled, Theme, useTheme } from '@mui/material/styles'
import { Menu } from './menu'
import { ChevronLeft } from '@mui/icons-material'
import PropertySelect from '../../components/property-select/property-select'
import UserMenu from '../../components/user-menu/user-menu'
import useBreakpoints from 'src/app/hooks/breakpoints/use-breakpoints'
import logo from '../../../assets/logo-ts-hospitality-white.png'
import jwt from 'jwt-decode'
import { TokenLoginInfo } from '../../types/login.types'
import { useAuth } from 'src/app/hooks/auth/auth.hook'
import { useReactiveVar } from '@apollo/client'
import { cookieVars } from 'src/app/components/cookies-selector/open-dialog-var'
import { useGrant } from 'src/app/components/authorization/grant-hooks'
import NotificationBell from 'src/app/components/notification-bell/notification-bell'
import { BuyNow } from 'src/app/components/trial/buy-now'
import { Debugger } from 'src/app/components/debugger/debug'
import { MeteringModule, Feature } from 'src/api/api.generated'
import ZendeskChat from 'src/app/components/zendesk/zendesk'
import BuyNowDrawer from 'src/app/components/trial/drawer/buy-now.drawer'
import { modules } from '@mobiscroll/react/dist/src/core/components/datepicker/datepicker'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'

const HiCAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isLarge',
})<{ open?: boolean; isLarge?: boolean }>(({ theme, open, isLarge }) => ({
  position: 'fixed',
  top: 0,
  bottom: 'unset',
  zIndex: theme.zIndex.drawer - 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open &&
    isLarge && {
      marginLeft: MENU_WIDTH,
      transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
}))

const MENU_WIDTH = 248

const openedMixin = (theme: Theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
})

const closedMixin = (theme: Theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
})

export const MenuDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isLarge',
})<{ open?: boolean; isLarge?: boolean }>(({ theme, open, isLarge }) => ({
  flexShrink: 0,
  whiteSpace: 'nowrap',
  marginTop: '48px',
  boxSizing: 'border-box',
  zIndex: theme.zIndex.drawer - 1,
  ...((open && {
    ...openedMixin(theme),
    width: `${MENU_WIDTH}px !important`,
    '& .MuiDrawer-paper': openedMixin(theme),
    '& .MuiDrawer-paperAnchorLeft': {
      width: `${MENU_WIDTH}px !important`,
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }) as any),
  ...((!open && {
    ...closedMixin(theme),
    width: isLarge ? `calc(${theme.spacing(6)} + 1px) !important` : '0px !important',
    '& .MuiDrawer-paper': closedMixin(theme),
    '& .MuiDrawer-paperAnchorLeft': {
      width: isLarge ? `calc(${theme.spacing(6)} + 1px) !important` : '0px !important',
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }) as any),
}))

/**
 * Layout delle pagine private, raggiungibili solo da autenticati
 */
export const PrivatePageLayout = (props: { children?: React.ReactNode; hideToolbar?: boolean; hideMenu?: boolean; disableMenu?: boolean }) => {
  // ------------------------------------------
  // TODO: TEMPORARY LAYOUT WAITING FOR 1FRONT
  // ------------------------------------------
  const navigate = useNavigate()
  const theme: Theme = useTheme()
  const { sm, md, lg } = useBreakpoints()
  const [menuOpen, setMenuOpen] = useState<boolean>()
  const { context, setCurrent, onboardingSetParams, /* getBuyNowUrl */ openBuyDrawer, isOpenBuyDrawer, closeBuyDrawer } = useContext(hicContext)
  const { auth, loginInfo } = useAuth()
  const cookieState = useReactiveVar(cookieVars)
  const { isInRole } = useGrant()
  const localizeString = useLocalizeString()

  const isTrial = useMemo(() => !context.loading && context.permissions?.trialModules && context.permissions?.trialModules.length > 0, [context])

  //--------- Zendesk ----------------

  useEffect(() => {
    if (!document.getElementById('ze-snippet')) {
      const script = document.createElement('script')
      script.id = 'ze-snippet'
      script.src = 'https://static.zdassets.com/ekr/snippet.js?key=0092e5c1-588d-45a4-aa65-f41cc77404e4'
      script.async = true
      document.body.appendChild(script)
      return () => {
        document.body.removeChild(script)
      }
    }
  }, [])

  useEffect(() => {
    if (!document.getElementById('module')) {
      const script_utils = document.createElement('script')
      script_utils.type = 'module'
      script_utils.src = '/static/scripts/zendesk-messenger-utils.js'
      document.body.appendChild(script_utils)
      return () => {
        document.body.removeChild(script_utils)
      }
    }
  }, [])

  useEffect(() => {
    try {
      if (isTrial || (!context.loading && context.tenantSellerName)) {
        window.zE('messenger', 'hide')
      } else {
        window.zE('messenger', 'show')
      }
    } catch (_) {
      console.log('zendesk not yet initialized!')
    }
  }, [isTrial, context])

  //--------- Zendesk ----------------

  useEffect(() => {
    if (props.hideMenu || props.disableMenu) {
      setMenuOpen(false)
    } else if (lg) {
      setMenuOpen(true)
    } else {
      setMenuOpen(false)
    }
  }, [lg, props.hideMenu, props.disableMenu])

  const allowDataTracking = useMemo(() => {
    return cookieState.cookieSettings?.C0005 ?? false
  }, [cookieState.cookieSettings])

  const hash = (string: string) => {
    const utf8 = new TextEncoder().encode(string)
    return crypto.subtle.digest('SHA-256', utf8).then((hashBuffer) => {
      const hashArray = Array.from(new Uint8Array(hashBuffer))
      const hashHex = hashArray.map((bytes) => bytes.toString(16).padStart(2, '0')).join('')
      return hashHex
    })
  }

  const gainsightLogin = useCallback(
    async (tenantId: string, tenantDescription: string, propertyId: string, propertyName: string) => {
      const contextInfo = !context.loading ? context : undefined

      if (loginInfo && contextInfo) {
        //passing user and account objects:
        // eslint-disable-next-line @typescript-eslint/no-extra-semi

        const userId = await hash(`${tenantId}#${loginInfo.email}`)
        const email = allowDataTracking ? loginInfo.email : await hash(loginInfo.email)
        const firstName = allowDataTracking ? loginInfo.given_name : await hash(loginInfo.given_name)
        const lastName = allowDataTracking ? loginInfo.family_name : await hash(loginInfo.family_name)

        const modulesDistinct = contextInfo.permissions?.modules?.reduce((acc: string[], curr) => (acc.includes(curr) ? acc : [...acc, curr]), []) ?? []
        const featuresDistinct =
          contextInfo.permissions?.features
            ?.filter((f) => f === Feature.ENTERPRISE || f === Feature.HOUSEKEEPER || f === Feature.AUTOMATION || f === Feature.AUTOMATION_API)
            ?.reduce((acc: string[], curr) => (acc.includes(curr) ? acc : [...acc, curr]), []) ?? []

        const trialModulesDistinct = (contextInfo.permissions?.trialModules?.reduce((acc: string[], curr) => (acc.includes(curr.module) ? acc : [...acc, curr.module]), []) ?? []).map(
          (m) => `TRIAL-${m}`,
        )

        const propertiesClassTypes = contextInfo.properties?.map((p) => (p.classType ? localizeString(p.classType.description, 'it') : '')) ?? []

        ;(window as any).aptrinsic(
          'identify',
          {
            //User Fields
            id: userId,
            email: email,
            firstName: firstName,
            lastName: lastName,
            signUpDate: Date.now(),
            role: contextInfo.permissions?.userRole?.permissions?.join('; '),
            tsid: loginInfo.sub,
          },
          {
            //Account Fields
            id: tenantId,
            name: `${tenantDescription} - ${propertyName}`,
            parentGroupId: propertyName,
            plan: [...modulesDistinct, ...featuresDistinct, ...trialModulesDistinct].join('; '),
            renewalDate: contextInfo.permissions?.trialModules?.[0]?.trialExpiration ?? '',
            nameVar: contextInfo.tenantSellerName,
            HIC_propertyClassType: propertiesClassTypes.join('; '),
          },
        )
      }
    },
    [loginInfo, JSON.stringify(context)],
  )

  const onPropertyChange = useCallback(
    (current: { tenantId: string; propertyId: string | null }) => {
      if (!context.loading) {
        const tenant = context.tenants.find((t) => t.id === current.tenantId)
        if (!tenant) return

        const tenantProperties = context.properties.filter((p) => p.tenantId === tenant.id)
        const property = tenantProperties.find((p) => p.id === current.propertyId) ?? null

        setCurrent(tenant, property, () => {
          console.log('------------------------------------------------------')
          console.log('property', property)
          console.log('context.currentTenant.backoffice', context.currentTenant.backoffice)
          console.log('tenant.backoffice', tenant.backoffice)
          console.log('------------------------------------------------------')
          if (property === null && !context.currentTenant.backoffice) {
            if (tenant.backoffice) navigate('/internal-support', { replace: true })
            else {
              navigate('/onboarding-property', { replace: true })
            }
          } else {
            if (context.currentTenant.backoffice) navigate('/')
          }
          //Gainsight property switch log
          if (property) gainsightLogin(tenant.id, tenant.description ?? '', property.id, property.name)
        })
      }
    },
    [context, setCurrent, gainsightLogin],
  )

  const onboardingSetParamsHandler = useCallback(
    (onboardingTenantId: string) => {
      onboardingSetParams(onboardingTenantId, () => {
        navigate('/onboarding-property', { replace: true })
      })
    },
    [onboardingSetParams],
  )

  useEffect(() => {
    if (!context.loading) {
      if (!context.currentProperty) {
        setMenuOpen(false)
        if (context.currentTenant.backoffice && isInRole(['SUPPORT', 'OWNER']) /*FIXME: ++++ IL RUOLO DEVE ESSERE SOLO SUPPORT +++++ */) {
          navigate('/internal-support', { replace: true })
        } else {
          if (context.currentTenant.id) navigate('/onboarding-property', { replace: true })
        }
      }
      // Gainsight login log
      if (context.currentProperty) {
        const { id: tenantId, description } = context.currentTenant
        const { id: propertyId, name } = context.currentProperty
        gainsightLogin(tenantId, description ?? '', propertyId, name)
      }
    }
  }, [context])

  const onMenuClick = useCallback(() => {
    setMenuOpen(!menuOpen)
  }, [menuOpen])

  const onMenuItemClick = useCallback(() => {
    if (!lg || props.hideMenu || props.disableMenu) {
      setMenuOpen(false)
    }
  }, [lg])

  const pmsTrialVersionExpiration = useMemo(() => {
    if (!context.loading && context.permissions?.trialModules) {
      return context.permissions?.trialModules.find((m) => m.module === MeteringModule.PMS)?.trialExpiration
    }
  }, [JSON.stringify(context)])

  if (context.loading) {
    return <HICLoading />
  } else {
    return (
      <Box>
        {/* <Debugger position={'bottom'}>{JSON.stringify(context.permissions)}</Debugger> */}
        <Box sx={{ display: 'flex', height: '100%' }} className="asd1">
          <>
            {!props.hideToolbar && (
              <HiCAppBar position="absolute" open={menuOpen} isLarge={lg && !props.hideMenu}>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                <Toolbar disableGutters {...({ variant: 'navigation' } as any)} sx={{ px: '0!important' }}>
                  <Box sx={{ flexGrow: 1, pl: 1.5 }}>
                    <Stack direction="row" alignItems="center" spacing={1} paddingLeft={1.5}>
                      {props.disableMenu ? undefined : (
                        <IconButton onClick={onMenuClick} edge="start" size="large" sx={{ width: 49, color: 'white' }}>
                          <FontAwesomeIcon fontSize={16} icon={faBars} />
                        </IconButton>
                      )}
                      <Link to="/" style={{ display: 'flex' }}>
                        <img src={logo} height={md ? 30 : 20} alt="TeamSystem Hospitality" />
                      </Link>
                    </Stack>
                  </Box>
                  {pmsTrialVersionExpiration && (
                    <Box sx={{ flexGrow: 1, pl: 1.5, height: '48px' }}>
                      <BuyNow expirationDate={pmsTrialVersionExpiration} onClick={openBuyDrawer} />
                    </Box>
                  )}
                  <Box sx={{ flexGrow: 0 }}>
                    <Stack direction="row" spacing={2} alignItems="center" pr={1}>
                      <NotificationBell />
                      <PropertySelect
                        tenants={context.tenants}
                        properties={context.properties}
                        value={{ tenantId: context.currentTenant.id, propertyId: context.currentProperty?.id ?? null }}
                        onChange={(p) => {
                          onPropertyChange(p)
                        }}
                        onOnboarding={onboardingSetParamsHandler}
                        tenantUnusedPlants={context.tenantUnusedPlants}
                        currentTenant={context.currentTenant}
                        currentPropertyId={context.currentProperty?.id}
                      />

                      <UserMenu />
                    </Stack>
                  </Box>
                </Toolbar>
              </HiCAppBar>
            )}
            <MenuDrawer
              variant="permanent"
              open={menuOpen && !props.hideMenu && !props.disableMenu}
              isLarge={lg && !props.hideMenu && !props.disableMenu}
              sx={{
                display: { xs: 'none', lg: 'block' },
                '& .MuiDrawer-paperAnchorLeft': {
                  marginTop: '48px',
                },
              }}
            >
              {context.currentProperty && <Menu onItemClick={onMenuItemClick} showTooltip={!menuOpen} tenantId={context.currentProperty.tenantId} propertyId={context.currentProperty.id} />}
            </MenuDrawer>
            <Drawer anchor="left" variant="temporary" open={menuOpen} sx={{ display: { xs: 'block', lg: 'none' } }} onBackdropClick={onMenuClick}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  padding: theme.spacing(0, 1),
                  height: 48,
                }}
              >
                <IconButton onClick={onMenuClick}>
                  <ChevronLeft />
                </IconButton>
              </Box>
              {context.currentProperty && <Menu onItemClick={onMenuItemClick} tenantId={context.currentProperty.tenantId} propertyId={context.currentProperty.id} />}
            </Drawer>
          </>
          <Box sx={{ flexGrow: 1, paddingTop: props.hideToolbar ? 'none' : '48px', height: '100%' }} className="asd2">
            {props.children}
          </Box>
        </Box>
        {/* TELEMETRY INFO - PLEASE USE ONLY IF NEEDED AND ONLY IN DEBUG! */}
        {/* <div
          style={{
            zIndex: 99999999,
            padding: 5,
            position: 'fixed',
            bottom: 10,
            left: 10,
            backgroundColor: '#000000aa',
            color: '#fff',
            height: '200px',
            width: '600px',
            overflow: 'auto',
            fontSize: 10,
          }}
        >
          <p>
            CURRENT TENANT:
            {context.currentTenant && JSON.stringify(context.currentTenant)}
          </p>
          <p>
            CURRENT PROPERTY:
            {context.currentProperty && JSON.stringify(context.currentProperty)}
          </p>
          <p>
            ONBOARDING TENANT:
            {context.onboardingTenantId && JSON.stringify(context.onboardingTenantId)}
          </p>
        </div> */}
        <BuyNowDrawer open={isOpenBuyDrawer} onClose={closeBuyDrawer} />
      </Box>
    )
  }
  // ------------------------------------------
}
